import { useMemo, useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";

export interface SeriesOption {
  key: string;
  label: string;
}

interface ReadingsSeriesPickerProps {
  options: SeriesOption[];
  selected: string[];
  onChange: (keys: string[]) => void;
}

/**
 * A filterable multi-select for raw readings series. Type to filter the list,
 * tick individual rows, or select/deselect every currently-matching row. The
 * select/deselect-all buttons act on the *filtered* subset, so a search of
 * "temperature" + "Select all matching" grabs exactly those.
 */
const ReadingsSeriesPicker = ({
  options,
  selected,
  onChange,
}: ReadingsSeriesPickerProps) => {
  const [filter, setFilter] = useState("");
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, filter]);

  // Preserve the canonical (options) ordering when emitting a new selection.
  const emit = (nextSet: Set<string>) =>
    onChange(options.filter((o) => nextSet.has(o.key)).map((o) => o.key));

  const toggle = (key: string) => {
    const next = new Set(selectedSet);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    emit(next);
  };

  const selectAllMatching = () => {
    const next = new Set(selectedSet);
    filtered.forEach((o) => next.add(o.key));
    emit(next);
  };

  const deselectAllMatching = () => {
    const next = new Set(selectedSet);
    filtered.forEach((o) => next.delete(o.key));
    emit(next);
  };

  const matchingSuffix = filter.trim() ? " matching" : "";

  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle
        variant="outline-secondary"
        size="sm"
        className="w-100 text-start d-flex justify-content-between align-items-center"
      >
        <span>
          {selected.length}/{options.length} series selected
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu
        style={{ width: "100%", minWidth: "280px", padding: "8px" }}
        renderOnMount
      >
        <Form.Control
          type="text"
          size="sm"
          placeholder="Filter readings..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-2"
          autoFocus
        />
        <div className="d-flex gap-2 mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={selectAllMatching}
            disabled={filtered.length === 0}
          >
            Select all{matchingSuffix}
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={deselectAllMatching}
            disabled={filtered.length === 0}
          >
            Deselect all{matchingSuffix}
          </Button>
        </div>
        <div style={{ maxHeight: "320px", overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <div className="text-muted small px-1 py-2">
              No matching readings
            </div>
          ) : (
            filtered.map((o) => (
              <Form.Check
                key={o.key}
                type="checkbox"
                id={`series-opt-${o.key}`}
                label={o.label}
                checked={selectedSet.has(o.key)}
                onChange={() => toggle(o.key)}
                className="small text-nowrap"
              />
            ))
          )}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ReadingsSeriesPicker;
